/**
 * One-shot in-app notice. Shows once per browser until dismissed.
 *
 * Minimal-viable notification surface — localStorage-backed, no table,
 * no API. Each distinct notice gets its own stable `noticeKey`; the
 * component keeps a dismissal timestamp in
 * `merkflow-notice-<noticeKey>` so the notice doesn't re-appear.
 *
 * Auto-expires 30 days after first render even if undismissed — stale
 * notices are noise. If a user clears localStorage they'll see it
 * again on next visit; that's acceptable.
 *
 * This is NOT a general-purpose notification center. If a second notice
 * ever needs to stack with this one, extract a queue; for now, one
 * notice, one key, localStorage. Don't generalise prematurely.
 */
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonAnchor } from '@/components/ui/Button';

interface OneTimeNoticeProps {
  /** Unique key identifying this notice. Stored as
   *  `merkflow-notice-<noticeKey>` in localStorage. */
  noticeKey: string;
  /** Whether the render conditions (plan, tier, etc.) currently match. */
  active: boolean;
  title: string;
  body: string;
  primaryLabel: string;
  /** Route path or full URL. Falsy = no primary button. */
  primaryHref?: string | null;
  secondaryLabel?: string;
  /** Called after dismissal via any path (x button, primary, secondary). */
  onDismiss?: () => void;
}

const STORAGE_PREFIX = 'merkflow-notice-';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export function OneTimeNotice({
  noticeKey,
  active,
  title,
  body,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  onDismiss,
}: OneTimeNoticeProps) {
  const [visible, setVisible] = useState(false);
  const storageKey = `${STORAGE_PREFIX}${noticeKey}`;

  useEffect(() => {
    if (!active) return;
    // Check stored timestamp — unset or older than TTL → show.
    const raw = localStorage.getItem(storageKey);
    if (raw === null) {
      setVisible(true);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isFinite(parsed) && Date.now() - parsed < THIRTY_DAYS_MS) {
      setVisible(false);
      return;
    }
    // Stale timestamp older than 30 days — refresh + don't re-show.
    // (If we wanted periodic re-prompts, we'd show here; we don't.)
    setVisible(false);
  }, [active, storageKey]);

  const dismiss = () => {
    localStorage.setItem(storageKey, String(Date.now()));
    setVisible(false);
    onDismiss?.();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.2 }}
          className="border-border bg-card/95 fixed top-4 right-4 z-40 w-full max-w-[400px] rounded-2xl border p-4 shadow-lg backdrop-blur-xl md:top-20 md:right-6"
          role="status"
        >
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="text-text-tertiary hover:text-text-primary absolute top-3 right-3 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="mb-2 flex items-center gap-2">
            <div className="bg-accent/15 flex h-7 w-7 items-center justify-center rounded-xl">
              <Sparkles className="text-accent h-4 w-4" />
            </div>
            <p className="font-heading text-text-primary text-base font-bold">{title}</p>
          </div>
          <p className="text-text-secondary mb-3 pr-4 text-sm">{body}</p>
          <div className="flex items-center gap-2">
            {primaryHref && (
              <ButtonAnchor
                href={primaryHref}
                onClick={dismiss}
                className="inline-flex items-center gap-1 text-sm"
              >
                {primaryLabel}
              </ButtonAnchor>
            )}
            {secondaryLabel && (
              <Button variant="ghost" onClick={dismiss} className="text-sm">
                {secondaryLabel}
              </Button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * V81 behaviour-change notice — Pro customers who had AI Visibility flipped
 * on overnight. Reads the active workspace's plan and suppresses on any
 * tier that already had the feature (Agency) or doesn't get it (Starter).
 * plan_override = true (admin-locked plans) is also suppressed — those
 * tend to be test workspaces.
 */
export function ProAiVisibilityLaunchedNotice({
  workspacePlan,
  planOverride,
}: {
  workspacePlan: string | undefined;
  planOverride: boolean | undefined;
}) {
  const { t } = useTranslation();
  const active = workspacePlan === 'pro' && planOverride !== true;
  return (
    <OneTimeNotice
      noticeKey="pro-ai-visibility-launched"
      active={active}
      title={t('notice.pro.ai_visibility.title')}
      body={t('notice.pro.ai_visibility.body')}
      primaryLabel={t('notice.pro.ai_visibility.primary')}
      primaryHref="/dashboard/ai-visibility"
      secondaryLabel={t('notice.pro.ai_visibility.secondary')}
    />
  );
}
